import { forwardRef, type JSX, type ReactNode } from 'react';

import {
  MaskingProps,
  type DataTestId,
  type StylingProps,
} from '@dynatrace/strato-components/core';

import { TreeMapInternal } from './TreeMapInternal.js';
import { TreeMapLegendRenderer } from './TreeMapLegend/TreeMapLegendRenderer.js';
import { TreeMapOverlay } from './TreeMapOverlay/TreeMapOverlay.js';
import { ChartLayout } from '../../core/components/chart-layout/ChartLayout.js';
import { useAutoLegendRefresh } from '../../core/components/chart-layout/hooks/useAutoLegendRefresh.js';
import { useChartLegend } from '../context/ChartLegendContext.js';
import { TreeMapAggregationDataProvider } from '../providers/TreeMapAggregationDataProvider.js';
import { TreeMapHighlightingMapProvider } from '../providers/TreeMapHighlightingMapProvider.js';
import { TreeMapInternalDataProvider } from '../providers/TreeMapInternalDataProvider.js';
import type { InternalTreeMapNode } from '../types/internal.js';

interface TreemapChartLayoutProps
  extends StylingProps,
    MaskingProps,
    DataTestId {
  data: InternalTreeMapNode;
  width: string | number;
  height: string | number;
  emptyState?: ReactNode;
  errorState: ReactNode | ((errorMessage: string) => JSX.Element) | undefined;
  isEmpty: boolean;
  loading: boolean;
}

export const TreeMapChartLayout = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  TreemapChartLayoutProps
>((props, forwardedRef) => {
  const {
    data,
    width,
    height,
    emptyState,
    errorState,
    isEmpty,
    loading,
    className,
    style,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'data-testid': dataTestId,
  } = props;

  const {
    hidden: legendHidden,
    position: legendPosition,
    ratio: legendRatio,
    onRatioChange,
  } = useChartLegend();

  const chartLayoutRef = useAutoLegendRefresh(data);

  return (
    <ChartLayout
      ref={chartLayoutRef}
      chartWidth={width}
      chartHeight={height}
      emptyState={emptyState}
      errorState={errorState}
      showLoader={loading}
      isEmpty={isEmpty}
      className={className}
      style={style}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      data-testid={dataTestId}
    >
      <ChartLayout.Graph>
        <TreeMapInternalDataProvider data={data}>
          <TreeMapAggregationDataProvider>
            <TreeMapHighlightingMapProvider>
              <div style={{ position: 'relative' }}>
                <TreeMapInternal ref={forwardedRef} />
                {loading ? null : <TreeMapOverlay />}
              </div>
            </TreeMapHighlightingMapProvider>
          </TreeMapAggregationDataProvider>
        </TreeMapInternalDataProvider>
      </ChartLayout.Graph>
      {!legendHidden && (
        <ChartLayout.Legend
          position={legendPosition}
          ratio={legendRatio}
          onResize={onRatioChange}
        >
          <TreeMapLegendRenderer />
        </ChartLayout.Legend>
      )}
    </ChartLayout>
  );
});
